import { batch } from 'react-redux';

import { DELAY_BETWEEN_MESSAGES } from '../../constants';
import { hasContent, isFunction, isUndefined } from '../../util/guards';
import { TAction, TMessage } from './createQueue';

type TCallback = (action: TAction) => void;

interface IAllActions {
  before: () => TAction[];
  after: () => TAction[];
  messages: TMessage[];
  notHandledMessageIds?: string[];
}

export class Queue {
  private cb: TCallback;
  private before: (() => TAction[]) | null;
  private after: (() => TAction[]) | null;
  private messages: TMessage[] = [];
  private isRunning = false;
  private listId: Set<string>;

  constructor(cb: TCallback) {
    this.cb = cb;
    this.listId = new Set();
  }

  private doBefore() {
    if (this.before) {
      this.run(this.before());
      this.before = null;
    }
  }

  private doAfter() {
    if (this.after) {
      this.run(this.after());
      this.after = null;
    }
  }

  private run(actions: TAction[]) {
    batch(() => {
      actions.forEach((action) => {
        this.cb(action);
      });
    });
  }

  private runMessages(): void {
    const action = this.messages.shift();

    /**
     * Internal message: it needs to run in the queue order.
     */
    if (isFunction(action)) {
      const result = action();

      if (!isUndefined(result)) {
        this.cb(result);
      }

      return this.runMessages();
    }

    if (!isUndefined(action)) {
      this.cb(action);
    }

    setTimeout(() => {
      if (this.messages.length > 0) {
        this.runMessages();
      } else {
        this.doAfter();
        this.isRunning = false;
      }
    }, DELAY_BETWEEN_MESSAGES);
  }

  hasHandled(id?: string): boolean {
    return hasContent(id) && this.listId.has(id);
  }

  addActions({ messages, before, after, notHandledMessageIds = [] }: IAllActions) {
    /**
     * We don't need to execute the actions `before` messages
     * if it comes when the queue is running.
     *
     * And we execute only the latest of the bunch of actions `after`
     * if it comes when the queue is running we skip the previous one.
     */
    notHandledMessageIds.forEach((_id) => this.listId.add(_id));
    this.before = before;
    this.after = after;
    this.messages.push(...messages);

    if (!this.isRunning) {
      this.isRunning = true;
      this.doBefore();
      this.runMessages();
    }
  }
}
